import type { CoinDenomination, CoinmakersVaultChallengeConfig } from './CoinmakersVaultConfig.js';

export type CoinmakersVaultActionResult = 'accepted' | 'coin-cap' | 'unavailable' | 'empty' | 'complete';
export type CoinmakersVaultCheckResult = 'complete' | 'wrong-total' | 'too-many-coins';

export interface CoinmakersVaultSerializedState {
  readonly version: 1;
  readonly coins: Readonly<Record<CoinDenomination, number>>;
  readonly hintStage: number;
  readonly attempts: number;
  readonly complete: boolean;
}

export interface CoinmakersVaultSnapshot extends CoinmakersVaultSerializedState {
  readonly totalCents: number;
  readonly coinCount: number;
  readonly remainingCents: number;
  readonly hint?: string;
}

const DENOMINATIONS: readonly CoinDenomination[] = [1, 5, 10, 25];

export class CoinmakersVaultState {
  private coins: Record<CoinDenomination, number> = { 1: 0, 5: 0, 10: 0, 25: 0 };
  private hintStage = 0;
  private attempts = 0;
  private completed = false;

  constructor(
    readonly config: CoinmakersVaultChallengeConfig,
    saved?: Partial<CoinmakersVaultSerializedState>,
  ) {
    if (saved?.version === 1) this.restore(saved);
  }

  addCoin(denomination: CoinDenomination): CoinmakersVaultActionResult {
    if (this.completed) return 'complete';
    if (!this.config.availableCoins.includes(denomination)) return 'unavailable';
    if (this.snapshot.coinCount >= 12) return 'coin-cap';
    this.coins[denomination] += 1;
    return 'accepted';
  }

  removeCoin(denomination: CoinDenomination): CoinmakersVaultActionResult {
    if (this.completed) return 'complete';
    if (this.coins[denomination] < 1) return 'empty';
    this.coins[denomination] -= 1;
    return 'accepted';
  }

  check(): CoinmakersVaultCheckResult {
    this.attempts += 1;
    if (this.snapshot.totalCents !== this.config.targetCents) return 'wrong-total';
    if (this.snapshot.coinCount > this.config.coinLimit) return 'too-many-coins';
    this.completed = true;
    return 'complete';
  }

  requestHint() {
    this.hintStage = Math.min(this.config.hints.length, this.hintStage + 1);
    return this.snapshot.hint;
  }

  serialize(): CoinmakersVaultSerializedState {
    return {
      version: 1,
      coins: { ...this.coins },
      hintStage: this.hintStage,
      attempts: this.attempts,
      complete: this.completed,
    };
  }

  get snapshot(): CoinmakersVaultSnapshot {
    const totalCents = DENOMINATIONS.reduce((sum, coin) => sum + coin * this.coins[coin], 0);
    const coinCount = DENOMINATIONS.reduce((sum, coin) => sum + this.coins[coin], 0);
    return {
      ...this.serialize(),
      totalCents,
      coinCount,
      remainingCents: this.config.targetCents - totalCents,
      hint: this.hintStage > 0 ? this.config.hints[this.hintStage - 1] : undefined,
    };
  }

  private restore(saved: Partial<CoinmakersVaultSerializedState>) {
    const counts = saved.coins;
    if (!counts || DENOMINATIONS.some(coin => !Number.isInteger(counts[coin]) || counts[coin] < 0)) return;
    const totalCount = DENOMINATIONS.reduce((sum, coin) => sum + Number(counts[coin]), 0);
    if (totalCount > 12) return;
    this.coins = { 1: counts[1], 5: counts[5], 10: counts[10], 25: counts[25] };
    this.hintStage = Math.min(this.config.hints.length, Math.max(0, Number(saved.hintStage) || 0));
    this.attempts = Math.max(0, Number(saved.attempts) || 0);
    const snapshot = this.snapshot;
    this.completed = saved.complete === true
      && snapshot.totalCents === this.config.targetCents
      && snapshot.coinCount <= this.config.coinLimit;
  }
}
